import { Box, Text } from '@air/zephyr';
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import { documentToReactComponents, Options } from '@contentful/rich-text-react-renderer';
import { Block, BLOCKS, Document, Inline, INLINES, MARKS } from '@contentful/rich-text-types';

import { ExternalLink } from '~/old-marketing/components/Link';
import { Bold } from '~/old-marketing/components/richText/Bold';
import { Italic } from '~/old-marketing/components/richText/Italic';
import { ListItem, OrderedList, UnorderedList } from '~/old-marketing/components/richText/List';
import { Underline } from '~/old-marketing/components/richText/Underline';

export const richTextRenderer = (content: Document, customOptions?: Options) => {
  const renderText = (text: string) => {
    return text.split('\n').reduce((children: any, textSegment: any, index: string | number) => {
      return [...children, index > 0 && <Box as="br" key={index} />, textSegment];
    }, []);
  };

  return documentToReactComponents(content, {
    /* eslint-disable react/display-name */
    renderMark: {
      [MARKS.BOLD]: (text) => <Bold>{text}</Bold>,
      [MARKS.ITALIC]: (text) => <Italic>{text}</Italic>,
      [MARKS.UNDERLINE]: (text) => <Underline>{text}</Underline>,
      ...customOptions?.renderMark,
    },
    renderNode: {
      [BLOCKS.HEADING_2]: (_, children) => (
        <Text
          as="h2"
          variant={['text-heading-32', 'text-heading-32', 'text-heading-40']}
          tx={{ color: 'jay700', mt: [48, 48, 60], mb: [24, 24, 16] }}
        >
          {children}
        </Text>
      ),
      [BLOCKS.LIST_ITEM]: (_, children) => <ListItem>{children}</ListItem>,
      [BLOCKS.OL_LIST]: (_, children) => <OrderedList>{children}</OrderedList>,
      [BLOCKS.PARAGRAPH]: (_, children) => (
        <Text
          as="p"
          tx={{
            mb: 24,
            color: 'inherit',
            fontFamily: 'inherit',
            fontSize: 'inherit',
            lineHeight: 'inherit',
            letterSpacing: 'inherit',

            '&:last-child': {
              mb: 0,
            },
          }}
        >
          {children}
        </Text>
      ),
      [BLOCKS.UL_LIST]: (_, children) => <UnorderedList>{children}</UnorderedList>,
      [INLINES.HYPERLINK]: (node, children) => {
        const { uri } = node?.data;

        return (
          <ExternalLink href={uri} tx={{ display: 'inline', fontSize: 'inherit' }}>
            {children}
          </ExternalLink>
        );
      },
      ...customOptions?.renderNode,
    },
    /* eslint-enable react/display-name */
    renderText: customOptions?.renderText ?? renderText,
  });
};

export const planTextRenderer = (content: Block | Inline) => documentToPlainTextString(content);
